import { useEffect, useState, useCallback, useMemo } from 'react';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Link, Navigate } from 'react-router-dom';
import { LifeBuoy, ArrowLeft, RefreshCw, Trash2, Check, Loader2, Mail, Reply } from 'lucide-react';

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS = (e) => {
  const s = e?.support_notify;
  if (!s) return null;
  if (s.sent) return { label: 'Notified', cls: 'bg-green/10 text-green ring-green/20' };
  return { label: 'Email failed', cls: 'bg-red-50 text-red-700 ring-red-200' };
};

export default function AdminContact() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open'); // open | resolved | all
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/contact-messages');
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error('Failed to load contact messages');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter(m => {
    if (statusFilter === 'open') return !m.resolved;
    if (statusFilter === 'resolved') return !!m.resolved;
    return true;
  }), [items, statusFilter]);

  const toggleResolved = async (m) => {
    setBusyId(m.id);
    try {
      await http.patch(`/admin/contact-messages/${m.id}`, { resolved: !m.resolved });
      setItems(prev => prev.map(x => x.id === m.id ? { ...x, resolved: !m.resolved } : x));
      toast.success(m.resolved ? 'Reopened' : 'Marked done');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusyId(null); }
  };

  const deleteItem = async (m) => {
    if (!window.confirm('Delete this message permanently?')) return;
    setBusyId(m.id);
    try {
      await http.delete(`/admin/contact-messages/${m.id}`);
      setItems(prev => prev.filter(x => x.id !== m.id));
      setTotal(t => Math.max(0, t - 1));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusyId(null); }
  };

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const openCount = items.filter(i => !i.resolved).length;
  const resolvedCount = items.filter(i => i.resolved).length;

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-contact-page">
      <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="back-to-admin">
        <ArrowLeft size={14}/> Back to Admin
      </Link>

      <div className="flex items-center gap-3">
        <LifeBuoy className="text-gold" size={22}/>
        <div>
          <h1 className="font-heading text-3xl">Support Inbox</h1>
          <p className="text-sm text-navy/55">Messages sent from the public Support page and the in-app Contact us screens. Reply from your email — each row shows the sender.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Total</p>
          <p className="font-heading text-2xl mt-1">{total}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Open</p>
          <p className="font-heading text-2xl mt-1 text-red-700">{openCount}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 card-soft">
          <p className="text-[11px] uppercase tracking-widest text-navy/55">Done</p>
          <p className="font-heading text-2xl mt-1 text-green">{resolvedCount}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 card-soft flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {['open', 'resolved', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`contact-filter-${s}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-smooth ring-1 ${
                statusFilter === s ? 'bg-navy text-white ring-navy' : 'bg-white text-navy/70 hover:text-navy ring-navy/10 hover:ring-navy/30'
              }`}
            >
              {s === 'open' ? 'Open' : s === 'resolved' ? 'Done' : 'All'}
            </button>
          ))}
        </div>
        <button onClick={load} className="ml-auto px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 transition-smooth flex items-center gap-2" data-testid="contact-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      <div className="space-y-3">
        {loading && (
          <div className="bg-white rounded-2xl p-8 text-center text-navy/45 card-soft">
            <Loader2 className="inline animate-spin" size={18}/>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-8 text-center text-navy/50 card-soft" data-testid="contact-empty">
            No messages here yet.
          </div>
        )}
        {!loading && filtered.map((m) => {
          const st = STATUS(m.emails);
          return (
            <div key={m.id} data-testid={`contact-item-${m.id}`} className={`bg-white rounded-2xl p-5 card-soft ${m.resolved ? 'opacity-70' : ''}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  {m.ref && <span className="text-[10px] font-mono text-navy/55 font-semibold">{m.ref}</span>}
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold ring-1 bg-beige text-navy ring-navy/10">
                    {m.topic || 'General'}
                  </span>
                  {m.resolved && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold ring-1 bg-green/10 text-green ring-green/20">
                      <Check size={10}/> Done
                    </span>
                  )}
                  {st && (
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 font-semibold ring-1 ${st.cls}`}>
                      <Mail size={10}/> {st.label}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-navy/40">{fmt(m.created_at)}</p>
              </div>

              <p className="mt-3 text-sm font-medium text-navy">{m.name || '—'}</p>
              <a href={`mailto:${m.email}?subject=Re:%20${encodeURIComponent(m.topic || 'Your message')}%20(${m.ref || ''})`} className="text-xs text-gold hover:underline inline-flex items-center gap-1" data-testid={`contact-reply-${m.id}`}>
                <Reply size={11}/> {m.email}
              </a>
              <p className="text-sm text-navy/85 mt-2 leading-relaxed whitespace-pre-wrap">{m.message}</p>

              {m.page_url && (
                <p className="text-[10px] text-navy/40 mt-2">From: <span className="font-mono">{m.page_url}</span></p>
              )}

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => toggleResolved(m)}
                  disabled={busyId === m.id}
                  className={`text-[12px] px-3 py-1.5 rounded-full transition-smooth disabled:opacity-50 ${
                    m.resolved ? 'bg-beige text-navy hover:bg-[#EAE5D9]' : 'bg-green/10 text-green hover:bg-green/20'
                  }`}
                  data-testid={`contact-toggle-${m.id}`}
                >
                  {busyId === m.id ? <Loader2 size={12} className="inline animate-spin"/> : <Check size={12} className="inline mr-1"/>}
                  {m.resolved ? 'Reopen' : 'Mark done'}
                </button>
                <button
                  onClick={() => deleteItem(m)}
                  disabled={busyId === m.id}
                  className="ml-auto text-[12px] px-3 py-1.5 rounded-full text-red-600 hover:bg-red-50 transition-smooth inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`contact-delete-${m.id}`}
                >
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
